import { useState, useMemo, useEffect } from 'react';
import { X, Save, Check, Info } from 'lucide-react';
import { fmt, computeDerived, CLASS_COLOR } from '../../lib/portfolio';
import { memberLabel } from '../../lib/theme';
import { apiGet, apiSend } from '../../lib/api';

const monthEnd = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); };
const currentYM = () => new Date().toISOString().slice(0, 7);
const fmtYM = ym => new Date(ym + '-01T00:00:00Z').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export default function UpdateActualsModal({ open, onClose, data, members, onSaved }) {
  const meta = data?.asset_meta || [];
  const entities = data?.entity_order?.length ? data.entity_order : ['PRANAV', 'RASHU', 'HUF'];
  const allHoldings = data?._allHoldings || null;
  const latestMonth = data?.latest_month;

  const [target, setTarget] = useState('current'); // 'current' | 'latest'
  const targetEnd = target === 'latest' && latestMonth ? latestMonth : monthEnd(currentYM());
  const sameMonth = latestMonth === monthEnd(currentYM());

  const [grid, setGrid] = useState(null);
  const [usdInr, setUsdInr] = useState(data?.usd_inr || 84);
  const [tab, setTab] = useState(entities[0]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [remind, setRemind] = useState(true);

  useMemo(() => {
    if (!open) return;
    const src = (allHoldings || []).filter(h => h.month_ending === targetEnd);
    // current month with no data yet → carry forward latest as a starting point
    const useRows = src.length ? src : (target === 'current' ? (allHoldings || []).filter(h => h.month_ending === latestMonth) : []);
    const g = {};
    entities.forEach(e => { g[e] = {}; meta.forEach(m => { g[e][m.asset] = 0; }); });
    useRows.forEach(h => { if (g[h.entity]) g[h.entity][h.asset === 'STOCKS_MF' ? 'STOCKS' : h.asset] = h.value; });
    setGrid(g);
    setRemind(true);
    setTab(entities[0]);
  }, [open, target]); // eslint-disable-line

  // live USD/INR on open
  useEffect(() => {
    if (!open) return;
    fetch('/fxlive').then(r => r.json()).then(d => { if (d.usd_inr) setUsdInr(Number(d.usd_inr.toFixed(2))); }).catch(() => {});
  }, [open]);

  const derived = useMemo(() => grid ? computeDerived(grid, meta, entities, null) : null, [grid, meta, entities]);
  if (!open || !grid) return null;

  const setCell = (e, a, v) => setGrid(g => ({ ...g, [e]: { ...g[e], [a]: v === '' ? 0 : Number(v) } }));
  const memberTotal = e => meta.reduce((s, m) => s + (grid[e]?.[m.asset] || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      // grid holds INR internally; US_STOCKS is sent in USD (server stores USD + INR@fx)
      const payload = {};
      for (const e of Object.keys(grid)) {
        payload[e] = {};
        for (const a of Object.keys(grid[e])) {
          payload[e][a] = a === 'US_STOCKS' ? (grid[e][a] || 0) / usdInr : grid[e][a];
        }
      }
      const r = await apiSend('/actuals', { month_ending: targetEnd, rows: payload, usd_inr: usdInr });
      const j = await r.json();
      if (j.ok) { setDone(true); setTimeout(() => onSaved?.(), 700); } else setSaving(false);
    } catch { setSaving(false); }
  };

  const darkVars = { '--text': '#f5f0e8', '--text-dim': 'rgba(255,255,255,0.55)', '--text-mute': 'rgba(255,255,255,0.4)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-lg rounded-2xl overflow-hidden" style={{
        ...darkVars, background: 'linear-gradient(150deg, #1e1c24 0%, #161419 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <h2 className="font-display" style={{ fontSize: '20px', fontWeight: 700, color: '#f5f0e8' }}>Update Data</h2>
            <p style={{ fontSize: '13px', color: 'var(--accent)', fontWeight: 600, marginTop: '2px' }}>{fmtYM(targetEnd.slice(0, 7))}</p>
          </div>
          <button onClick={onClose} style={{ color: 'rgba(255,255,255,0.5)' }}><X size={22} /></button>
        </div>

        {/* month target: latest existing vs current (only when they differ) */}
        {!sameMonth && latestMonth && (
          <div className="flex items-center gap-1 mx-6 mt-4 p-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {[['latest', `Latest · ${fmtYM(latestMonth.slice(0, 7))}`], ['current', `This Month · ${fmtYM(currentYM())}`]].map(([k, lbl]) => (
              <button key={k} onClick={() => setTarget(k)} className="flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                style={{ background: target === k ? 'var(--accent-grad)' : 'transparent', color: target === k ? '#fff' : 'rgba(255,255,255,0.55)' }}>
                {lbl}
              </button>
            ))}
          </div>
        )}

        {/* reminder alert */}
        {remind && (
          <div className="flex items-center gap-3 px-6 py-3" style={{ background: 'rgba(249,115,22,0.12)', borderBottom: '1px solid rgba(249,115,22,0.2)' }}>
            <Info size={18} style={{ color: '#fb923c', flexShrink: 0 }} />
            <span style={{ color: '#f5f0e8', fontSize: '13px', flex: 1 }}>Remember to update each member — {entities.map(e => memberLabel(e, members)).join(', ')}.</span>
            <button onClick={() => setRemind(false)} className="px-3 py-1 rounded-lg text-xs font-semibold" style={{ background: 'var(--accent-grad)', color: '#fff' }}>OK</button>
          </div>
        )}

        <div className="px-6 py-5" style={{ overflowY: 'auto' }}>
          {/* USD + networth */}
          <div className="flex items-end justify-between mb-4">
            <div>
              <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>USD / INR</label>
              <input type="number" step="0.01" value={usdInr} onChange={e => setUsdInr(Number(e.target.value))}
                className="px-3 py-2 rounded-lg text-sm outline-none w-28"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#f5f0e8' }} />
            </div>
            <div className="text-right">
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Networth</p>
              <p className="font-display" style={{ fontSize: '24px', fontWeight: 700, color: '#34d399' }}>{derived ? fmt(derived.networth) : '—'}</p>
            </div>
          </div>

          {/* member tabs */}
          <div className="flex items-center gap-1 p-1 rounded-full mb-4" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {entities.map(e => (
              <button key={e} onClick={() => setTab(e)} className="flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all"
                style={{ background: tab === e ? 'var(--accent-grad)' : 'transparent', color: tab === e ? '#fff' : 'rgba(255,255,255,0.55)' }}>
                {memberLabel(e, members)}
              </button>
            ))}
          </div>

          {/* active member's asset inputs */}
          <div className="space-y-2.5">
            {meta.map(m => {
              const isUsd = m.asset === 'US_STOCKS';
              const inr = grid[tab][m.asset] || 0;
              const shown = isUsd ? (inr ? Math.round(inr / usdInr) : '') : (inr || '');
              return (
                <div key={m.asset} className="flex items-center gap-3">
                  <div className="flex-1">
                    <span style={{ color: '#f5f0e8', fontSize: '14px', fontWeight: 500 }}>{m.label}</span>
                    <span style={{ color: CLASS_COLOR[m.class], fontSize: '11px', marginLeft: '8px', fontFamily: 'monospace' }}>{m.class}</span>
                    {isUsd && inr > 0 && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginLeft: '8px', fontFamily: 'monospace' }}>≈ {fmt(inr)}</span>}
                  </div>
                  <div className="relative" style={{ width: '160px' }}>
                    {isUsd && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>$</span>}
                    <input type="number" value={shown} placeholder={isUsd ? '0 (USD)' : '0'}
                      onChange={ev => setCell(tab, m.asset, ev.target.value === '' ? '' : (isUsd ? Number(ev.target.value) * usdInr : ev.target.value))}
                      className="px-3 py-2 rounded-lg text-right text-sm outline-none w-full"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#f5f0e8', fontFamily: 'monospace', paddingLeft: isUsd ? '22px' : '12px' }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{memberLabel(tab, members)} total</span>
            <span style={{ color: '#fb923c', fontWeight: 700, fontFamily: 'monospace', fontSize: '14px' }}>{fmt(memberTotal(tab))}</span>
          </div>
        </div>

        <div className="px-6 py-4 flex justify-end gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>Cancel</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: done ? 'rgba(52,211,153,0.2)' : 'var(--accent-grad)', color: done ? '#34d399' : '#fff' }}>
            {done ? <><Check size={16} /> Saved</> : <><Save size={16} /> {saving ? 'Saving…' : 'Save Month'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
